import moment from 'moment';

/** The bits of a just-created job the booking toast reads. */
export interface BookedJob {
  _id?: string;
  status?: string | null;
  dropoffDate?: string | null;
  animalSpecies?: string | null;
  headCount?: number | null;
  hasMissingCutsheets?: boolean | null;
  requestedBy?: {
    first_name?: string | null;
    last_name?: string | null;
    company?: string | null;
  } | null;
  vendor?: { shop_name?: string | null } | null;
  /**
   * Server-counted capacity on the drop-off day, after this booking — from
   * the create mutation's payload, not the job.
   */
  bookingFollowUp?: {
    dayCapacity: number;
    daySpotsRemaining: number;
    nextOpenDate?: string | null;
  } | null;
}

/**
 * "1 of 8 spots left that day." / "That was the last spot that day — next
 * open day is Tue, Oct 27." Null for a closed day or no facts. Reads after a
 * sentence that already names the day.
 */
export function capacityLine(
  followUp: BookedJob['bookingFollowUp'],
): string | null {
  if (!followUp || followUp.dayCapacity <= 0) return null;
  const left = followUp.daySpotsRemaining;
  if (left > 0) {
    return `${left} of ${followUp.dayCapacity} spot${followUp.dayCapacity === 1 ? '' : 's'} left that day.`;
  }
  const next = followUp.nextOpenDate
    ? ` — next open day is ${moment.utc(followUp.nextOpenDate).format('ddd, MMM D')}`
    : '';
  return `That was the last spot that day${next}.`;
}

/**
 * Still waiting on the processor (or a deposit) — not a confirmed date yet.
 * EnumProcessorSchedulingStatus values, spelled out: this module is imported
 * by tests that mock `lib/graphql`.
 */
const AWAITING = new Set<string>(['Requested', 'Initial', 'Pending_Deposit']);

/**
 * Title + body for a new booking. The title is the event, short enough to
 * read at a glance; who, what and when go in the body, followed by the one
 * thing that has to happen next (the processor confirming, or cut sheets).
 */
export function jobBookedToast(
  job: BookedJob,
  audience: 'processor' | 'producer',
): { title: string; body: string } {
  const when = job.dropoffDate
    ? moment.utc(job.dropoffDate).format('ddd, MMM D')
    : null;
  const what =
    job.headCount && job.animalSpecies
      ? `${job.headCount} ${job.animalSpecies.toLowerCase()}`
      : null;
  const cutsheetsNeeded = !!job.hasMissingCutsheets;
  const onDate = when ? ` for drop-off on ${when}` : '';

  if (audience === 'producer') {
    const processor = job.vendor?.shop_name || 'the processor';
    if (job.status && AWAITING.has(job.status)) {
      return {
        title: 'Request sent',
        body: `You've asked ${processor} to take ${what ?? 'your animals'}${
          when ? ` on ${when}` : ''
        }. They'll confirm the date, and we'll let you know.`,
      };
    }
    return {
      title: "You're booked in",
      body: [
        `${processor} has ${what ? `your ${what}` : 'you'} down${onDate}.`,
        cutsheetsNeeded
          ? 'Next: fill out your cut sheets before cut day.'
          : "You're all set.",
      ].join(' '),
    };
  }

  const who =
    [job.requestedBy?.first_name, job.requestedBy?.last_name]
      .filter(Boolean)
      .join(' ')
      .trim() || job.requestedBy?.company;
  const verb = job.headCount === 1 ? 'is' : 'are';
  const subject = who
    ? `${who}'s ${what ?? 'job'} ${what ? verb : 'is'}`
    : what
      ? `${what} ${verb}`
      : 'The job is';
  // What the day looks like now, then what's still to do. "It's on your
  // calendar" only when there's nothing else to say.
  const capacity = when ? capacityLine(job.bookingFollowUp) : null;
  let next: string | null = null;
  if (cutsheetsNeeded) next = 'Cut sheets are still needed before cut day.';
  else if (!capacity) next = "It's on your calendar.";
  return {
    title: 'Job booked',
    body: [`${subject} booked in${onDate}.`, capacity, next]
      .filter(Boolean)
      .join(' '),
  };
}
